import React, { useState, useEffect, useRef } from 'react'
import { MdFullscreen, MdFullscreenExit } from 'react-icons/md'
import { styled, ThemeProvider } from 'styled-components'
import useComponentSize from '@rehooks/component-size'
import SoundEffects from './sound/SoundEffects'
import { theme } from './theme'
import { PlayerView } from './components'
import { ConnectedAudio } from './sound/ConnectedMedia'
import { MISSION_PHASES } from '@mission.io/mission-toolkit/constants'
import type { PlayerProps, Props } from '@mission.io/mission-player'
import { isPaused, useOnDeath } from './context'
import {
	Provider as PlayerProvider,
	useMissionStateSelector,
	useOnVocalTrackEnd,
	useResolvers,
	getMissionPhase,
	getScreen,
	useCurrentVocalTrack,
	useVolumeOf,
} from './context'
import { VOLUMES } from './utility'
import { DIALOG_PARENT_ID } from './components/basics/Dialog.jsx'
import { useSilentMissionPhaseRef } from './sound/hooks'
import { CanvasConnectionProvider } from './components/views/Screens/CollaborativeCulminatingMomentScreen/CreativeCanvas/CanvasConnectionProvider'
import { CssVariableThemeDefinitions } from './components/CssVariablesThemeDefinitions'
import './main.css'
/**
 * The player component which we export for this module. The player plays all visuals for a mission and plays audio as
 * well unless otherwise specified (by providing the 'silent' prop)
 * This component takes a large selection of props and passes them to a context provider for the
 * rest of the player.
 * @param {Props} props
 */
export default function PlayerWithProvider(props: Props): React$Node {
	const { goFullScreen, ...providerProps } = props
	return (
		<PlayerProvider {...providerProps}>
			<CanvasConnectionProvider>
				<Player goFullScreen={goFullScreen} silent={providerProps.silent} />
			</CanvasConnectionProvider>
		</PlayerProvider>
	)
}

/**
 * The player must exist within the PlayerProvider context to function properly. It show all screens, videos, vocal tracks, overlays, and sound effects for a mission.
 * It also shows the end of mission sequence and a login screen before the mission starts
 * @param {PlayerProps} props
 */
function Player(props: PlayerProps) {
	const currentScreen = useMissionStateSelector(getScreen)
	const missionPhase = useMissionStateSelector(getMissionPhase)

	useOnDeath()

	const [isFullscreen, setFullscreen] = useState()
	const { goFullScreen } = props

	useEffect(() => {
		if (goFullScreen && isFullscreen !== undefined && isFullscreen !== null) {
			goFullScreen(isFullscreen)
		}

		const handleKeyEvent = event => {
			if (event.key === 'F11' || event.key === 'f') {
				event.preventDefault()
				event.stopPropagation()

				setFullscreen(!isFullscreen)
			}

			if (event.key === 'Escape' && isFullscreen) {
				setFullscreen(false)
			}
		}

		window.addEventListener('keyup', handleKeyEvent)

		return () => {
			window.removeEventListener('keyup', handleKeyEvent)
		}
	}, [isFullscreen, goFullScreen])

	const renderFullScreenIcon = () => {
		return (
			<FullScreenWrapper>
				{isFullscreen ? (
					<MdFullscreenExit onClick={() => setFullscreen(false)} />
				) : (
					<MdFullscreen onClick={() => setFullscreen(true)} />
				)}
			</FullScreenWrapper>
		)
	}

	const playerRef = useRef(null)
	const wrapperSize = useComponentSize(playerRef)

	const shouldShowLogin =
		missionPhase === MISSION_PHASES.LOGIN ||
		missionPhase === MISSION_PHASES.TRAINING

	return (
		<PlayerWrapper
			id={DIALOG_PARENT_ID}
			ref={playerRef}
			onDoubleClick={() => setFullscreen(!isFullscreen)}>
			<ThemeProvider theme={theme}>
				<CssVariableThemeDefinitions>
					{shouldShowLogin || (missionPhase && currentScreen) ? (
						<>
							{props.goFullScreen && renderFullScreenIcon()}
							{!props.silent && <Audio />}

							<PlayerView
								wrapperSize={wrapperSize}
								shouldShowLogin={shouldShowLogin}
							/>
						</>
					) : (
						<NotLoadedMessage>Mission Not Loaded</NotLoadedMessage>
					)}
				</CssVariableThemeDefinitions>
			</ThemeProvider>
		</PlayerWrapper>
	)
}

// A component that plays all audio for mission-player
function Audio() {
	const isMissionPaused = useMissionStateSelector(isPaused)
	const vocalTrackRef = useSilentMissionPhaseRef()
	const currentVocalTrack = useCurrentVocalTrack()
	const { vocalTrackSourceResolver } = useResolvers()
	const onVocalTrackEnd = useOnVocalTrackEnd()
	const vocalTrackVolume = useVolumeOf(VOLUMES.VOCAL_TRACK, false)

	return (
		<div>
			{currentVocalTrack && (
				<ConnectedAudio
					vocalTrackId={currentVocalTrack._actionId}
					ref={vocalTrackRef}
					autoPlay={!isMissionPaused}
					applyMissionPause={false}
					src={vocalTrackSourceResolver(currentVocalTrack.url)}
					onEnded={() => {
						onVocalTrackEnd(currentVocalTrack._actionId)
					}}
					volume={vocalTrackVolume}
				/>
			)}
			<SoundEffects />
		</div>
	)
}

const FullScreenWrapper = styled.div`
	position: absolute;
	left: 0;
	bottom: 0;
	right: 0;
	opacity: 0;
	display: flex;
	flex-direction: row-reverse;
	padding: 4px ${({ theme }) => theme.spacing};
	background: rgba(0, 0, 0, 0.7);
	z-index: ${({ theme }) => theme.zAlwaysShow};

	transition: opacity 500ms;

	&:hover {
		opacity: 1;
	}

	svg {
		color: #fff;
		font-size: 30px;
		cursor: pointer;

		&:hover {
			opacity: 0.7;
		}
	}
`

const PlayerWrapper = styled.div.attrs({ className: 'font-game-body' })`
	height: 100%;
	width: 100%;
	max-width: 100%;
	max-height: 100%;
	overflow: hidden;
	background-color: black;

	* {
		box-sizing: border-box;
	}
`

const NotLoadedMessage = styled.p`
	color: #fff;
	text-align: center;
	${({ theme }) => `
		font-size: ${theme.fontxl};
		margin-top: ${theme.spacing6x};
	`}
`
